import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import {
  Upload,
  Download,
  Delete,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Edit,
  Close,
} from "@mui/icons-material";
import theme from "../../utils/theme";
import AppButton from "../Common/AppButton";
import toast from "react-hot-toast";

const styles = {
  container: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: 3,
  },
  sidebar: { p: 2, width: { xs: "100%", md: 280 }, flexShrink: 0 },
};

export default function PDFSignatureApp({
  pdfPages,
  currentPage,
  setCurrentPage,
  signatures,
  setSignatures,
  loading,
  setLoading,
  error,
  setError,
  resetFcn,
  savePdfToDatabase,
  selectedDoc,
  pdfLoadingError,
  rfi_id,
}) {
  const [signatureImage, setSignatureImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [signatureDialog, setSignatureDialog] = useState(false);

  const pdfContainerRef = useRef(null);
  const signatureCanvasRef = useRef(null);

  // Get canvas coordinates for both mouse and touch events
  const getCanvasCoordinates = (clientX, clientY) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);

    ctx.lineTo(x, y);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);

    ctx.lineTo(x, y);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL("image/png");
    setSignatureImage(dataUrl);
    setSignatureDialog(false);
    toast.success("Signature saved! Click on the PDF to place it.");
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignatureImage(e.target.result);
        setSignatureDialog(false);
        toast.success("Signature uploaded! Click on the PDF to place it.");
      };
      reader.readAsDataURL(file);
    }
  };

  // Place signature on PDF
  const placeSignature = (e) => {
    if (!signatureImage || !pdfPages.length) return;

    const container = pdfContainerRef.current;
    const img = container.querySelector("img");

    if (!img) return;

    // Get the image's bounding rect (this accounts for the zoom transform)
    const imgRect = img.getBoundingClientRect();

    // Calculate position relative to the original (unscaled) image dimensions
    const x = (e.clientX - imgRect.left) / zoom;
    const y = (e.clientY - imgRect.top) / zoom;

    // Ensure the signature is placed within the image bounds
    if (x >= 0 && y >= 0 && x <= img.naturalWidth && y <= img.naturalHeight) {
      setSignatures([
        ...signatures,
        {
          image: signatureImage,
          x,
          y,
          page: currentPage,
          width: 150,
          height: 50,
        },
      ]);
    }
  };

  const removeSignature = (index) => {
    setSignatures(signatures.filter((_, i) => i !== index));
  };

  // Generate signed PDF (shared logic)
  const generateSignedPDF = async () => {
    if (!pdfPages.length) return null;

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    for (let i = 0; i < pdfPages.length; i++) {
      if (i > 0) pdf.addPage();

      const img = new Image();
      img.src = pdfPages[i];
      await new Promise((resolve) => {
        img.onload = () => {
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          pdf.addImage(img, "JPEG", 0, 0, pageWidth, pageHeight, null, "FAST");

          const pageSignatures = signatures.filter((sig) => sig.page === i);
          pageSignatures.forEach((sig) => {
            const scaleX =
              pageWidth / (pdfContainerRef.current?.offsetWidth || 1);
            const scaleY =
              pageHeight / (pdfContainerRef.current?.offsetHeight || 1);

            pdf.addImage(
              sig.image,
              "PNG",
              sig.x * scaleX,
              sig.y * scaleY,
              sig.width * scaleX,
              sig.height * scaleY,
              null,
              "FAST"
            );
          });
          resolve();
        };
      });
    }

    return pdf;
  };

  // Download signed PDF
  const downloadSignedPDF = async () => {
    setLoading(true);
    try {
      const pdf = await generateSignedPDF();
      if (!pdf) return;

      pdf.save(selectedDoc.fileName || "signed-document.pdf");
      toast.success("PDF downloaded successfully!");
    } catch (err) {
      setError("Failed to generate PDF");
      console.error(err);
    }
    setLoading(false);
  };

  // Save signed PDF to database
  const saveSignedPDF = async () => {
    setLoading(true);
    try {
      const pdf = await generateSignedPDF();
      if (!pdf) return;

      const pdfBlob = pdf.output("blob");

      const formData = new FormData();
      formData.append("file", pdfBlob, "signed-document.pdf");
      formData.append(
        "document_name",
        selectedDoc.fileName || "signed-document.pdf"
      );
      formData.append("uploaded_document_id", selectedDoc.uploaded_document_id);
      formData.append("rfi_id", rfi_id);

      const res = await savePdfToDatabase(formData);

      if (res.success) {
        toast.success(res.message || "PDF saved successfully to database!");
        resetFcn();
      } else {
        toast.error(res.message || "Failed to save PDF to database");
      }
    } catch (err) {
      setError("Failed to save PDF to database");
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Load PDF.js library
    if (!document.querySelector('script[src*="pdf.js"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      document.head.appendChild(script);
    }

    // Load jsPDF library
    if (!document.querySelector('script[src*="jspdf"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      document.head.appendChild(script);
    }
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.secondary.main,
          borderRadius: "6px 6px 0 0",
        }}
      >
        <Toolbar>
          <Edit sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PDF Signature Tool
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
            {pdfLoadingError && (
              <AppButton
                label="Back to Documents"
                onClick={resetFcn}
                buttonType="greyed"
              />
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* PDF Viewer and Controls */}
        {pdfPages?.length > 0 && (
          <Box sx={styles.container}>
            {/* Sidebar */}
            <Paper sx={styles.sidebar}>
              <Typography variant="h6" gutterBottom>
                Controls
              </Typography>

              <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 6, md: 12 }}>
                  <AppButton
                    fullWidth
                    label="Back to Documents"
                    onClick={resetFcn}
                    buttonType="greyed"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 12 }}>
                  <AppButton
                    fullWidth
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setSignatureDialog(true)}
                    label="Add Signature"
                    buttonType="primary"
                  />
                </Grid>

                <Grid size={{ xs: 6, md: 12 }}>
                  <AppButton
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={downloadSignedPDF}
                    disabled={signatures.length === 0}
                    label="Download PDF"
                    buttonType="secondary"
                  />
                </Grid>

                <Grid size={{ xs: 6, md: 12 }}>
                  <AppButton
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={saveSignedPDF}
                    disabled={signatures.length === 0}
                    label="Save Signed PDF"
                    buttonType="secondary"
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>
                Zoom: {Math.round(zoom * 100)}%
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <IconButton onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}>
                  <ZoomOut />
                </IconButton>
                <Slider
                  value={zoom}
                  onChange={(e, v) => setZoom(v)}
                  min={0.5}
                  max={2}
                  step={0.1}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => setZoom(Math.min(2, zoom + 0.1))}>
                  <ZoomIn />
                </IconButton>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Page {currentPage + 1} of {pdfPages.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<NavigateBefore />}
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                >
                  Prev
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<NavigateNext />}
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pdfPages.length - 1, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pdfPages.length - 1}
                >
                  Next
                </Button>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Signatures:{" "}
                {signatures.filter((s) => s.page === currentPage).length} on
                this page
              </Typography>
              {signatures
                .filter((s) => s.page === currentPage)
                .map((sig, idx) => {
                  const globalIdx = signatures.indexOf(sig);
                  return (
                    <Box
                      key={globalIdx}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Signature {globalIdx + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeSignature(globalIdx)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  );
                })}
            </Paper>

            {/* PDF Viewer */}
            <Paper
              sx={{
                flex: 1,
                p: 2,
                overflow: "auto",
                maxHeight: "80vh",
                cursor: signatureImage ? "crosshair" : "default",
              }}
            >
              <Box
                ref={pdfContainerRef}
                sx={{
                  position: "relative",
                  display: "inline-block",
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                }}
                onClick={placeSignature}
              >
                <img
                  src={pdfPages[currentPage]}
                  alt={`Page ${currentPage + 1}`}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    userSelect: "none",
                  }}
                />

                {/* Render signatures */}
                {signatures
                  .filter((sig) => sig.page === currentPage)
                  .map((sig, idx) => (
                    <img
                      key={idx}
                      src={sig.image}
                      alt="Signature"
                      style={{
                        position: "absolute",
                        left: sig.x,
                        top: sig.y,
                        width: sig.width,
                        height: sig.height,
                        cursor: "move",
                        border: "2px dashed #1976d2",
                        pointerEvents: "none",
                      }}
                    />
                  ))}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Signature Dialog */}
        <Dialog
          open={signatureDialog}
          onClose={() => setSignatureDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Create or Upload Signature
            <IconButton
              onClick={() => setSignatureDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <canvas
                ref={signatureCanvasRef}
                width={600}
                height={200}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={stopDrawing}
                style={{
                  border: "2px solid #ccc",
                  borderRadius: 4,
                  cursor: "crosshair",
                  width: "100%",
                  height: "auto",
                  touchAction: "none", // Prevent browser touch actions
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button variant="outlined" onClick={clearSignature} fullWidth>
                Clear
              </Button>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="signature-upload"
                type="file"
                onChange={handleSignatureUpload}
              />
              <label htmlFor="signature-upload" style={{ width: "100%" }}>
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<Upload />}
                >
                  Upload Image
                </Button>
              </label>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignatureDialog(false)}>Cancel</Button>
            <Button onClick={saveSignature} variant="contained">
              Save Signature
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
